import { useMemo, useState, useEffect } from 'react';
import type {
  BrawdleAttributeKey,
  BrawdleAttributeStatus,
  BrawdleGameStatus,
  BrawdleGuessResult,
} from '../../../shared/types/api';

const statusEmoji: Record<BrawdleAttributeStatus | 'correct' | 'wrong', string> = {
  correct: '🟩',
  close: '🟨',
  wrong: '⬛',
  higher: '🔼',
  lower: '🔽',
};

type ShareButtonProps = {
  puzzleId: string;
  results: BrawdleGuessResult[];
  status: BrawdleGameStatus;
  maxGuesses: number;
  attributesOrder: BrawdleAttributeKey[];
  copyLabel: string;
  copiedLabel: string;
  disabled?: boolean;
};

export const ShareButton = ({
  puzzleId,
  results,
  status,
  maxGuesses,
  attributesOrder,
  copyLabel,
  copiedLabel,
  disabled = false,
}: ShareButtonProps) => {
  const [copied, setCopied] = useState(false);
  const shareText = useMemo(() => {
    if (results.length === 0) {
      return '';
    }

    let triesLabel: string;
    if (status === 'won') {
      triesLabel = `${results.length}/${maxGuesses}`;
    } else if (status === 'lost') {
      triesLabel = `X/${maxGuesses}`;
    } else {
      triesLabel = `${results.length}/${maxGuesses}`;
    }

    const headerPrefix =
      status === 'playing'
        ? `Brawdle ${puzzleId} (${results.length}/${maxGuesses})`
        : `Brawdle ${puzzleId} ${triesLabel}`;
    const grid = results
      .map((result) =>
        attributesOrder
          .map((key) => statusEmoji[result[key] as BrawdleAttributeStatus] ?? statusEmoji.wrong)
          .join('')
      )
      .join('\n');

    return `${headerPrefix}\n${grid}`;
  }, [attributesOrder, maxGuesses, puzzleId, results, status]);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async () => {
    if (!shareText || disabled) {
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      console.warn('Clipboard API indisponible.');
      return;
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
    } catch (error) {
      console.error('Impossible de copier le résultat', error);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={!shareText || disabled}
      className="share-button w-full rounded-2xl bg-emerald-600 px-4 py-3 text-center font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {copied ? copiedLabel : copyLabel}
    </button>
  );
};

